import type { RxJsonSchema } from 'rxdb'

/**
 * Task Document Type (Denormalized for RxDB)
 * Note: Description fields moved to TaskContent for separate sync
 */
export interface TaskDocument {
  id: string
  envId: string
  projectId?: string | null
  seqNo?: number | null
  identifier?: string | null
  name: string
  // Description fields moved to TaskContent
  statusId: string
  userId?: string | null
  accountableUserId?: string | null
  priority?: number | null
  dueDate?: number | null
  dueDateType?: 'date' | 'datetime' | null
  startDate?: number | null
  startDateType?: 'date' | 'datetime' | null
  isAllDay: boolean
  sortOrder?: string | null
  estimation?: number | null
  isSomeday: boolean
  isDeleted: boolean
  isArchived: boolean
  parentId?: string | null
  workItemTypeId?: string | null

  // Cover image fields
  coverImageUrl?: string | null
  coverImageType?: 'unsplash' | 'custom' | null
  coverImageMeta?: string | null

  // Denormalized fields for performance (embedded from joins)
  statusName?: string
  statusColor?: string
  userName?: string | null
  userEmail?: string | null
  accountableUserName?: string | null
  accountableUserEmail?: string | null

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number // Required for sync!
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null
  archivedOn?: number | null
  archivedBy?: string | null

  // Super Agent per-task gate override ('plan_first' | 'direct' | null → inherit project default)
  aiGate?: 'plan_first' | 'direct' | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for Task
 */
export const taskSchema: RxJsonSchema<TaskDocument> = {
  version: 2,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    projectId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    seqNo: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 999999999,
      multipleOf: 1,
    },
    identifier: {
      type: ['string', 'null'],
      maxLength: 50,
    },
    name: {
      type: 'string',
      maxLength: 500,
    },
    // Description fields removed - now in TaskContent
    statusId: {
      type: 'string',
      maxLength: 36,
    },
    userId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    // Any future picker UI for accountableUserId must be hidden in
    // Personal-edition workspaces (workspaceType === 1), same gating as the
    // primary `userId` assignee — see useWorkspaceMode().
    accountableUserId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    priority: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 4,
    },
    dueDate: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
    },
    dueDateType: {
      type: ['string', 'null'],
      enum: ['date', 'datetime', null],
    },
    startDate: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
    },
    startDateType: {
      type: ['string', 'null'],
      enum: ['date', 'datetime', null],
    },
    isAllDay: {
      type: 'boolean',
      default: false,
    },
    estimation: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999,
    },
    sortOrder: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    isSomeday: {
      type: 'boolean',
      default: false,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },
    isArchived: {
      type: 'boolean',
      default: false,
    },
    parentId: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    workItemTypeId: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // Cover image fields
    coverImageUrl: {
      type: ['string', 'null'],
      maxLength: 2048,
    },
    coverImageType: {
      type: ['string', 'null'],
      enum: ['unsplash', 'custom', null],
    },
    coverImageMeta: {
      type: ['string', 'null'],
      maxLength: 2048,
    },

    // Denormalized fields
    statusName: {
      type: 'string',
      maxLength: 100,
      default: '',
    },
    statusColor: {
      type: 'string',
      maxLength: 20,
      default: '#gray',
    },
    userName: {
      type: ['string', 'null'],
      maxLength: 200,
    },
    userEmail: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    accountableUserName: {
      type: ['string', 'null'],
      maxLength: 200,
    },
    accountableUserEmail: {
      type: ['string', 'null'],
      maxLength: 255,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1, // Required for indexed number fields
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    archivedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    archivedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },
    aiGate: {
      type: ['string', 'null'],
      enum: ['plan_first', 'direct', null],
      maxLength: 20,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'name',
    'statusId',
    'isSomeday', // Required because it's used in index
    'isDeleted', // Required because it's used in index
    'isArchived', // Required because it's used in index
    'isAllDay',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'envId',
    'statusId',
    // 'userId', // Removed - nullable fields cannot be indexed in RxDB
    'updatedOn', // Critical for sync checkpoint
    ['envId', 'isDeleted'],
    ['envId', 'statusId'],
    // ['envId', 'userId'], // Removed - userId is nullable
    ['envId', 'isSomeday'],
    ['envId', 'isArchived'],
    ['envId', 'updatedOn'],
  ],
}

/**
 * Migration strategies for task schema.
 *
 * v0 -> v1: backfill fields added since the original schema —
 *   `seqNo`, `identifier` (nullable), `isAllDay` (required boolean, defaults
 *   to false), `userEmail`, `accountableUserEmail` (nullable).
 * v1 -> v2: added `aiGate` (Super Agent per-task gate override; nullable,
 *   defaults null → inherit project default).
 */
export const taskMigrationStrategies = {
  1: (oldDoc: any): any => ({
    ...oldDoc,
    seqNo: oldDoc.seqNo ?? null,
    identifier: oldDoc.identifier ?? null,
    isAllDay: oldDoc.isAllDay ?? false,
    userEmail: oldDoc.userEmail ?? null,
    accountableUserEmail: oldDoc.accountableUserEmail ?? null,
  }),
  2: (oldDoc: any): any => ({
    ...oldDoc,
    aiGate: oldDoc.aiGate ?? null,
  }),
}
